/** @jsxImportSource @emotion/react */
import { useMemo } from "react";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { TablaFiltrable, formatMonto } from "../../../shared";
import { itemsDelPeriodo, montoDe } from "./calculos";
import { fechaLocal } from "./periodos";

const ESTADOS = {
  Anulada: "danger",
  Cobrada: "success",
  Pendiente: "warning"
};

const estadoDe = (i) => (i.anulada ? "Anulada" : i.fechaPago ? "Cobrada" : "Pendiente");

const distintos = (valores) => [...new Set(valores.filter((v) => v != null && v !== ""))];

/**
 * Agrupa los ítems en una fila por factura. El monto es la suma de los ítems
 * que pasaron los filtros de la pantalla: con un filtro de tipo de
 * certificado, una factura que mezcla tipos muestra solo la parte de ese tipo.
 */
function filasPorFactura(items, opciones) {
  const porFactura = new Map();
  for (const i of items) {
    if (!porFactura.has(i.facturaId)) porFactura.set(i.facturaId, []);
    porFactura.get(i.facturaId).push(i);
  }

  return [...porFactura.values()].map((itemsFactura) => {
    const primero = itemsFactura[0];
    const descripciones = distintos(itemsFactura.map((i) => i.descripcion));
    const procesos = distintos(itemsFactura.map((i) => i.procesoNombre));
    const tipos = distintos(
      itemsFactura.map((i) => i.certificadoTipoNombre ?? "Sin tipo de certificado")
    );
    const monto = itemsFactura.reduce((suma, i) => suma + montoDe(i, opciones), 0);

    return {
      facturaId: primero.facturaId,
      numeroFactura: primero.numeroFactura,
      clienteNombre: primero.clienteNombre,
      _fecha: fechaLocal(primero.fecha),
      _fechaPago: fechaLocal(primero.fechaPago),
      _estado: estadoDe(primero),
      _descripciones: descripciones,
      _descripcion: descripciones.join("; "),
      _procesos: procesos.join(", "),
      _certificadoTipos: tipos,
      _certificadoTipo: tipos.join(", "),
      _monto: Math.round(monto * 100) / 100
    };
  });
}

/**
 * Una fila por factura emitida en el período elegido. Las anuladas aparecen
 * marcadas, aunque no sumen en los totales.
 */
export default function TablaDetalle({ facturas, rango, opciones, simbolo }) {
  const filas = useMemo(
    () => filasPorFactura(itemsDelPeriodo(facturas, rango), opciones),
    [facturas, rango, opciones]
  );

  const sufijoIva = opciones.mostrarConIva ? "" : " (sin IVA)";

  return (
    <Card>
      <TablaFiltrable
        value={filas}
        dataKey="facturaId"
        size="small"
        rows={15}
        sortField="_fecha"
        sortOrder={-1}
        nombreArchivo="indicadores_facturacion_detalle"
        emptyMessage="No hay facturas en el período"
        searchFields={["numeroFactura", "clienteNombre", "_descripcion", "_procesos"]}
        filtros={[
          { campo: "_estado", label: "Estado", labelPlural: "estados" },
          { campo: "clienteNombre", label: "Cliente", labelPlural: "clientes" },
          {
            campo: "_certificadoTipos",
            label: "Tipo de certificado",
            labelPlural: "tipos",
            multiple: true
          }
        ]}
      >
        {/* Las columnas ocultas no se ven en la tabla pero van al Excel. */}
        <Column header="Fecha" field="_fecha" sortable />
        <Column header="Factura" field="numeroFactura" sortable />
        <Column header="Cliente" field="clienteNombre" sortable />
        <Column
          header="Descripción"
          field="_descripcion"
          body={(f) =>
            f._descripciones.map((d, i) => <div key={i}>{d}</div>)
          }
          exportValue={(f) => f._descripcion}
        />
        <Column header="Proceso" field="_procesos" hidden />
        <Column header="Tipo de certificado" field="_certificadoTipo" hidden />
        <Column
          header="Estado"
          field="_estado"
          body={(f) => <Tag value={f._estado} severity={ESTADOS[f._estado]} />}
          sortable
        />
        <Column header="Fecha de pago" field="_fechaPago" hidden />
        <Column
          header={`Monto${sufijoIva}`}
          field="_monto"
          body={(f) => `${simbolo} ${formatMonto(f._monto)}`}
          exportValue={(f) => (f._estado === "Anulada" ? 0 : f._monto)}
          align="right"
          sortable
        />
        {/* Solo para el Excel: en el Monto las anuladas van en 0 para que la
            columna se pueda sumar, y su valor original queda acá. */}
        <Column
          header={`Monto anulado${sufijoIva}`}
          hidden
          exportValue={(f) => (f._estado === "Anulada" ? f._monto : "")}
        />
      </TablaFiltrable>
    </Card>
  );
}
